import type { FileFormatScope } from '@steerfi/shared';
import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createFileFormat } from '@/api/file-formats';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { FileFormatForm, type FileFormatFormHandle } from './file-format-form';
import type { FileFormatFormValues } from './file-format-form.types';
import { emptyFormValues, toCreatePayload } from './file-format-form.utils';
import { useFileFormatSubmit } from './use-file-format-submit';

export interface LockedScope {
  scope: FileFormatScope;
  partyId: string;
  partyName?: string | undefined;
  partyCode?: string | undefined;
}

interface AddFileFormatModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
  lockedScope?: LockedScope;
}

export const AddFileFormatModal: React.FC<AddFileFormatModalProps> = ({
  open,
  onOpenChange,
  onSuccess,
  lockedScope,
}) => {
  const initialValues = useMemo(
    () =>
      lockedScope
        ? { ...emptyFormValues(), scope: lockedScope.scope, party_id: lockedScope.partyId }
        : emptyFormValues(),
    [lockedScope]
  );
  const [values, setValues] = useState<FileFormatFormValues>(() => initialValues);
  const formRef = useRef<FileFormatFormHandle>(null);

  const handleSuccess = useCallback(() => {
    onOpenChange(false);
    onSuccess();
  }, [onOpenChange, onSuccess]);

  const { isLoading, fieldErrors, isValid, reportJsonValidity, handleSubmit, reset } =
    useFileFormatSubmit({
      values,
      validateJsonEditors: () => formRef.current?.validateJsonEditors() ?? true,
      perform: (vals) => createFileFormat(toCreatePayload(vals)),
      onSuccess: handleSuccess,
    });

  useEffect(() => {
    if (!open) {
      setValues(initialValues);
      reset();
    }
  }, [open, reset, initialValues]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[600px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Add File Format</DialogTitle>
            <DialogDescription>
              Define how the platform parses or generates a payment file.
            </DialogDescription>
          </DialogHeader>
          <FileFormatForm
            ref={formRef}
            values={values}
            onChange={setValues}
            fieldErrors={fieldErrors}
            readOnlyScope={
              lockedScope
                ? {
                    scope: lockedScope.scope,
                    party: lockedScope.partyName
                      ? {
                          display_name: lockedScope.partyName,
                          client_code: lockedScope.partyCode ?? null,
                        }
                      : null,
                    label: 'locked',
                  }
                : undefined
            }
            onJsonValidityChange={reportJsonValidity}
          />
          <DialogFooter className="mt-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading || !isValid}>
              {isLoading ? 'Creating...' : 'Create'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
